import { Badge, Button, tokens, Tooltip } from '@fluentui/react-components';
import {
  bundleIcon,
  Checkmark16Filled,
  Copy16Filled,
  Copy16Regular,
  Dismiss16Filled,
} from '@fluentui/react-icons';

import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard/useCopyToClipboard';
import { getEvalAverage, getSkillPassCount } from '../../../utils/sigsParser';

import type { InteractionListProps } from './InteractionList.interface';
import { useStyles } from './InteractionList.style';

const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;

function agentBadgeColor(
  agentType: string,
): 'brand' | 'important' | 'informative' {
  switch (agentType) {
    case 'PA':
      return 'brand';
    case 'MA':
      return 'important';
    default:
      return 'informative';
  }
}

function evalColor(avg: number | null): string {
  if (avg == null) return tokens.colorNeutralForeground3;
  if (avg >= 4) return tokens.colorPaletteGreenForeground1;
  if (avg >= 3) return tokens.colorPaletteYellowForeground1;
  return tokens.colorPaletteRedForeground1;
}

function CvCopyButton({ cv }: { cv: string }) {
  const styles = useStyles();
  const { copied, copy } = useCopyToClipboard();

  return (
    <Tooltip content={copied ? 'Copied!' : 'Copy CV'} relationship="label">
      <Button
        className={styles.copyBtn}
        appearance="subtle"
        size="small"
        icon={<CopyIcon />}
        onClick={(e) => {
          e.stopPropagation();
          copy(cv);
        }}
        aria-label="Copy CV to clipboard"
      />
    </Tooltip>
  );
}

export function InteractionList({ data, onSelect }: InteractionListProps) {
  const styles = useStyles();

  // Sort by timestamp descending, keeping track of original indices
  const sorted = data
    .map((item, originalIndex) => ({ item, originalIndex }))
    .sort(
      (a, b) =>
        new Date(b.item.creationTime).getTime() -
        new Date(a.item.creationTime).getTime(),
    );

  return (
    <div className={styles.root}>
      <table className={styles.table}>
        <thead className={styles.tableHead}>
          <tr>
            <th className={styles.th} style={{ width: '40px' }}>
              #
            </th>
            <th className={styles.th} style={{ width: '160px' }}>
              Timestamp
            </th>
            <th className={styles.th} style={{ width: '60px' }}>
              Agent
            </th>
            <th className={styles.th} style={{ width: '180px' }}>
              CV
            </th>
            <th className={styles.th}>User Query</th>
            <th className={styles.th} style={{ width: '80px' }}>
              Skills
            </th>
            <th className={styles.th} style={{ width: '80px' }}>
              Eval
            </th>
            <th className={styles.th} style={{ width: '80px' }}>
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((entry, idx) => {
            const item = entry.item;
            const { passed, total } = getSkillPassCount(item);
            const allPass = passed === total && total > 0;
            const evalAvg = getEvalAverage(item);
            const localTime = item.creationTime
              ? new Date(item.creationTime).toLocaleString()
              : '\u2014';
            const utcTime = item.creationTime
              ? new Date(item.creationTime).toISOString()
              : '';

            return (
              <tr
                key={item.id || idx.toString()}
                className={styles.tr}
                onClick={() => onSelect(entry.originalIndex)}
              >
                <td className={styles.td}>{(idx + 1).toString()}</td>
                <td className={styles.td}>
                  <Tooltip content={utcTime} relationship="description">
                    <span>{localTime}</span>
                  </Tooltip>
                </td>
                <td className={styles.td}>
                  <Badge
                    appearance="filled"
                    color={agentBadgeColor(item.agentType)}
                    size="small"
                  >
                    {item.friendlyAgentName}
                  </Badge>
                </td>
                <td className={styles.td}>
                  <div className={styles.cvCellWrapper}>
                    <Tooltip content={item.cv} relationship="description">
                      <span className={styles.cvCell}>{item.cv}</span>
                    </Tooltip>
                    {item.cv && <CvCopyButton cv={item.cv} />}
                  </div>
                </td>
                <td className={styles.td}>
                  <span className={styles.queryCell}>
                    {item.userPrompt
                      ? item.userPrompt.slice(0, 80) +
                        (item.userPrompt.length > 80 ? '...' : '')
                      : '(empty)'}
                  </span>
                </td>
                <td className={styles.td}>
                  <span className={styles.skillCell}>
                    {total > 0
                      ? `${passed.toString()}/${total.toString()}`
                      : '\u2014'}
                    {total > 0 &&
                      (allPass ? (
                        <Checkmark16Filled
                          style={{
                            color: tokens.colorPaletteGreenForeground1,
                          }}
                        />
                      ) : (
                        <Dismiss16Filled
                          style={{
                            color: tokens.colorPaletteRedForeground1,
                          }}
                        />
                      ))}
                  </span>
                </td>
                <td className={styles.td}>
                  <span
                    className={styles.evalCell}
                    style={{ color: evalColor(evalAvg) }}
                  >
                    {evalAvg != null ? evalAvg.toFixed(1) : '\u2014'}
                  </span>
                </td>
                <td className={styles.td}>
                  <Badge
                    appearance="tint"
                    color={item.status === 'Completed' ? 'success' : 'warning'}
                    size="small"
                  >
                    {item.status || 'Unknown'}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
